import React from 'react';
import styled from 'styled-components';
import moment from 'utils/moment';
import { monthLabel, rangeDateLabel, daysBetween } from '../services/format';
import { IconChevronLeft, IconChevronRight, IconCalendar, IconInfo } from './icons';

interface Props {
  viewYear: number;
  viewMonth: number; // 0-indexed
  rangeStart: string; // 'YYYY-MM-DD', always set
  rangeEnd: string;
  pickPhase: 'start' | 'end';
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onPickDay: (dayKey: string) => void;
}

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/** Date-range picker: tap a start day, then an end day. The range is the query. */
const MonthCalendarFilter: React.FC<Props> = ({
  viewYear, viewMonth, rangeStart, rangeEnd, pickPhase, onPrevMonth, onNextMonth, onPickDay,
}) => {
  const first = moment({ year: viewYear, month: viewMonth, day: 1 });
  const startDow = (first.day() + 6) % 7; // Monday-first
  const daysInMonth = first.daysInMonth();
  const todayKey = moment().format('YYYY-MM-DD');
  const dayCount = daysBetween(rangeStart, rangeEnd);

  const cells: Array<{ key: string; num: number } | null> = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ key: `${viewYear}-${pad(viewMonth + 1)}-${pad(d)}`, num: d });
  }

  return (
    <Panel>
      <PanelHead>
        <SectionLabel>Filter by Date</SectionLabel>
      </PanelHead>

      <Card>
        <MonthNav>
          <NavBtn onClick={onPrevMonth} aria-label="Previous month"><IconChevronLeft size={15} strokeWidth={2.4} /></NavBtn>
          <span className="month">{monthLabel(viewYear, viewMonth)}</span>
          <NavBtn onClick={onNextMonth} aria-label="Next month"><IconChevronRight size={15} strokeWidth={2.4} /></NavBtn>
        </MonthNav>
        <DowRow>
          {DOW.map((d, i) => <span key={i}>{d}</span>)}
        </DowRow>
        <Grid>
          {cells.map((c, i) => {
            if (!c) return <span key={`b${i}`} />;
            const isEdge = c.key === rangeStart || c.key === rangeEnd;
            const inRange = c.key >= rangeStart && c.key <= rangeEnd;
            return (
              <DayCell
                key={c.key}
                $edge={isEdge}
                $inRange={inRange}
                $today={c.key === todayKey}
                onClick={() => onPickDay(c.key)}
              >
                {c.num}
              </DayCell>
            );
          })}
        </Grid>
      </Card>

      <Banner>
        <IconCalendar size={18} color="var(--acc)" />
        <span className="label">{rangeDateLabel(rangeStart, rangeEnd)}</span>
        <span className="count">{dayCount > 1 ? `${dayCount} days` : '1 day'}</span>
      </Banner>

      <Hint $end={pickPhase === 'end'}>
        <IconInfo size={13} strokeWidth={2.2} />
        <span>
          {pickPhase === 'end'
            ? 'Now tap the end day — or leave it for a single day.'
            : 'Tap a day to start. Tap a second day for a date range.'}
        </span>
      </Hint>
    </Panel>
  );
};

export default MonthCalendarFilter;

const Panel = styled.div`
  width: 340px;
  flex-shrink: 0;
  border-right: 1px solid var(--border-1);
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-1);
`;

const Card = styled.div`
  border: 1px solid var(--border-1);
  border-radius: var(--r-lg);
  padding: 14px 16px;
  background: var(--bg-surface);
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  .month { font-size: 15px; font-weight: 700; color: var(--fg-1); }
`;

const NavBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: var(--r-sm);
  border: none;
  background: var(--bg-subtle);
  color: var(--fg-2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--border-1); }
`;

const DowRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
  margin-bottom: 3px;
  span {
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: var(--fg-4);
    padding: 4px 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
`;

const DayCell = styled.button<{ $edge: boolean; $inRange: boolean; $today: boolean }>`
  position: relative;
  height: 40px;
  border: none;
  border-radius: 9px;
  font-size: 13.5px;
  font-family: inherit;
  cursor: pointer;
  font-weight: ${p => (p.$edge ? 700 : 600)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => (p.$edge ? 'var(--acc)' : p.$inRange ? 'var(--acc-soft)' : 'transparent')};
  color: ${p => (p.$edge ? '#fff' : p.$inRange ? '#001848' : p.$today ? 'var(--acc)' : '#2C334B')};
  box-shadow: ${p => (!p.$edge && !p.$inRange && p.$today ? 'inset 0 0 0 1.5px var(--acc)' : 'none')};
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--r-lg);
  background: var(--acc-soft50);
  border: 1px solid var(--acc-soft200);
  .label { font-size: 14px; font-weight: 700; color: var(--fg-1); }
  .count { font-size: 12px; color: var(--fg-3); margin-left: auto; white-space: nowrap; }
`;

const Hint = styled.div<{ $end: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${p => (p.$end ? 'var(--acc)' : 'var(--fg-3)')};
  svg { flex-shrink: 0; }
`;
